import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DropDown,
  FormField,
  Link,
  PasswordField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { Controller, useForm } from 'react-hook-form';
import useSupportChat from 'shared/hooks/useSupportChat';
import { FetchOpsgenieApi, OpsgenieIntegrationForm } from 'shared/types';
import { IntegrationError } from 'settings/types';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import TestIntegrationSection from '../IntegrationTestSections/TestIntegrationSection';
import OpsgenieTestSection from '../IntegrationTestSections/OpsgenieTestSection';

const tokenPlaceHolder = 'xxxxxxxxxxxxxxxxxxxxxx';
const defaultRegionApiUrl = 'https://api.opsgenie.com/v2';
const euRegionApiUrl = 'https://api.eu.opsgenie.com/v2';

type Props = {
  open: boolean;
  onClose: () => void;
  data?: FetchOpsgenieApi;
  onOpsgenieIntegrationSubmit: (data: OpsgenieIntegrationForm) => void;
  error?: IntegrationError;
  loading?: boolean;
  onDisconnect?: () => void;
};

const OpsgenieDialog = ({
  open,
  onClose,
  data,
  onOpsgenieIntegrationSubmit,
  error,
  loading,
  onDisconnect,
}: Props) => {
  const [showError, setShowError] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const supportChat = useSupportChat();

  const defaultValues = useMemo(
    () => ({
      token: data?.integrated ? tokenPlaceHolder : null,
      regionApiUrl: data?.regionApiUrl
        ? data?.regionApiUrl
        : defaultRegionApiUrl,
    }),
    [data],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    setValue,
    watch,
    reset,
    control,
  } = useForm<OpsgenieIntegrationForm>({ defaultValues });

  useEffect(() => {
    if (error) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [error]);

  const resetForm = useCallback(() => {
    setShowError(false);
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect Opsgenie"
      onSubmit={handleSubmit((formData) => {
        const token = watch('token');
        if (token === tokenPlaceHolder) {
          formData.token = null;
        }
        onOpsgenieIntegrationSubmit(formData);
      })}
      reset={resetForm}
      error={error}
      loading={loading}
    >
      <FormField title="API key" errorMessage={errors.token?.message}>
        <PasswordField
          {...register('token', {
            required: requiredLabel,
          })}
          isFormValueDirty={dirtyFields.token}
          clearFormValue={() => setValue('token', '')}
          placeholder="API key"
        />
      </FormField>
      <FormField
        title="Select API URL"
        errorMessage={errors.regionApiUrl?.message}
      >
        <Controller
          name="regionApiUrl"
          control={control}
          defaultValue={defaultRegionApiUrl}
          render={({ field }) => (
            <DropDown
              size="large"
              options={[
                {
                  value: defaultRegionApiUrl,
                  label: `Default (${defaultRegionApiUrl})`,
                },
                { value: euRegionApiUrl, label: `EU (${euRegionApiUrl})` },
              ]}
              {...field}
            />
          )}
        />
      </FormField>
      {showError && (
        <Alert
          severity="error"
          style={{ fontWeight: themeTokens.fontWeight.bold }}
        >
          Elementary Opsgenie app failed to connect, please{' '}
          <Link onClick={supportChat.show} href="#">
            contact us
          </Link>{' '}
          for further assistance.
        </Alert>
      )}

      <TestIntegrationSection isSuccess={testSuccess}>
        <OpsgenieTestSection
          token={watch('token') === tokenPlaceHolder ? null : watch('token')}
          regionApiUrl={watch('regionApiUrl')}
          onSuccess={() => setTestSuccess(true)}
          onReset={() => setTestSuccess(false)}
        />
      </TestIntegrationSection>
      {data?.integrated && onDisconnect && (
        <Button
          variant="text"
          color="error"
          onClick={onDisconnect}
          sx={{ alignSelf: 'flex-start', mt: 1 }}
        >
          Disconnect Opsgenie
        </Button>
      )}
    </IntegrationDialog>
  );
};

export default OpsgenieDialog;
